import { useState, useCallback, useEffect, useRef } from 'react'
import { Head, router } from '@inertiajs/react'
import { Plus, MessageSquare, Handshake, BadgeCheck } from 'lucide-react'
import { MainLayout } from '@/components/layouts'
import { PostCard, CreatePostModal } from '@/components/community'
import { EditableContent } from '@/components/EditableContent'
import { SearchBar, Pagination } from '@/components/ui'
import type { CommunityPost, EditableContent as EditableContentType, Pagination as PaginationType } from '@/types'

interface MedecinsPharmaciensProps {
  official_posts: CommunityPost[]
  posts: CommunityPost[]
  pagination: PaginationType
  editable_banner: EditableContentType | null
  query: string | null
}

const ROUTE = '/medecins-pharmaciens'

export default function MedecinsPharmaciens({ official_posts, posts, pagination, editable_banner, query }: MedecinsPharmaciensProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState(query || '')
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedSearch = useCallback((value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      router.get(ROUTE, value ? { q: value } : {}, {
        preserveState: true,
        preserveScroll: true
      })
    }, 300)
  }, [])

  const handlePageChange = useCallback((page: number) => {
    const params: Record<string, string | number> = { page }
    if (searchQuery) params.q = searchQuery

    router.get(ROUTE, params, {
      preserveState: true,
      preserveScroll: false
    })
  }, [searchQuery])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    debouncedSearch(value)
  }

  return (
    <MainLayout title="Passerelle Santé">
      <Head title="Passerelle Santé" />

      {/* Banner éditable (si existe) */}
      {editable_banner && (
        <EditableContent
          content={editable_banner}
          wrapperClassName="mb-8 -mt-8 -mx-4"
        />
      )}

      {/* En-tête de l'espace partagé */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-gradient-to-r from-tessan-cream to-tessan-yellow/40 p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 flex h-11 w-11 items-center justify-center rounded-full bg-white text-tessan-primary">
            <Handshake className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">
              Passerelle Santé
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Le lieu d'échange commun aux deux communautés : posez vos questions, partagez vos
              retours d'expérience et discutez ensemble, médecins comme pharmaciens.
            </p>
          </div>
        </div>
      </div>

      {/* Barre de recherche (raccourcie) + bouton créer, sur la même ligne */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <SearchBar
          value={searchQuery}
          onChange={handleSearch}
          placeholder="Rechercher dans tout l'espace..."
          className="flex-1"
        />
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-tessan-primary text-white rounded-lg hover:bg-tessan-primary-hover transition-colors font-medium whitespace-nowrap"
        >
          <Plus className="w-4 h-4" />
          Créer un post
        </button>
      </div>

      {/* Split : Contenu Tessan (gauche) / Échanges pros (droite) */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 items-start">
        {/* Colonne gauche : Contenu Tessan */}
        <section>
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-tessan-primary" />
              <h2 className="font-heading text-xl font-bold text-gray-900">
                Contenu Tessan
              </h2>
            </div>
          </div>
          {official_posts.length > 0 ? (
            <div className="space-y-4">
              {official_posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <BadgeCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune publication Tessan pour le moment.</p>
            </div>
          )}
        </section>

        {/* Colonne droite : Échanges entre professionnels */}
        <section>
          <div className="mb-4">
            <h2 className="font-heading text-xl font-bold text-gray-900">
              Échanges entre professionnels
            </h2>
          </div>

          {/* Fil des discussions */}
          {posts.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-4">
                {posts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
              <Pagination pagination={pagination} onPageChange={handlePageChange} />
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery ? 'Aucun résultat' : 'Aucune discussion'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchQuery
                  ? `Aucune discussion trouvée pour "${searchQuery}"`
                  : 'Soyez le premier à lancer une discussion entre médecins et pharmaciens !'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-tessan-primary text-white rounded-lg hover:bg-tessan-primary-hover transition-colors font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Créer un post
                </button>
              )}
            </div>
          )}
        </section>
      </div>

      {/* Modale de création (espace partagé : pas de catégorie, flag interpro) */}
      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        categories={[]}
        interpro
      />
    </MainLayout>
  )
}
